import { ApiError, apiFetch, getAccessToken, getCurrentTenant } from '@graal/api';

/**
 * Rapport d'erreur de la console vers `POST /client-errors` (`reportClientError`) — l'API **du
 * déploiement lui-même** (first-party), jamais un tiers : graal-api journalise et compte, rien ne part
 * chez l'éditeur. La promesse « aucune télémétrie » du site tient.
 *
 * Ce qui part : la sorte (`render` : frontière d'erreur ; `query` / `mutation` : `onError` des caches
 * React Query), un message, la pile, le CHEMIN de la route (jamais la query string ni le fragment), l'agent
 * utilisateur, la référence de corrélation de l'API et la version de la console. **Jamais de donnée
 * saisie** : d'une erreur d'API, seuls le statut et le code partent (`HTTP 500 INTERNAL_ERROR`), pas le
 * message du serveur, qui peut citer une valeur envoyée.
 *
 * Garde-fous :
 * - rien sans session (le point d'entrée est authentifié) ;
 * - seulement ce qui est une panne : rendu qui lève, 5xx hors 501, échec réseau — pas un refus (4xx),
 *   pas une fonction coupée (501), pas une base occupée (503/504, déjà retentée et affichée) ;
 * - dédoublonnage (même sorte, même message, même première ligne de pile : un seul envoi par session) ;
 * - regroupement : les envois partent `DEBOUNCE_MS` après le dernier signalement, l'un après l'autre ;
 * - plafond par session d'onglet (`MAX_PER_SESSION`, compté dans `sessionStorage`, un rechargement ne le
 *   remet pas à zéro) ;
 * - **jamais en boucle** : l'envoi ne passe pas par React Query (ses échecs n'atteignent aucun `onError`),
 *   ne signale jamais rien lui-même, et s'arrête pour la session au premier 429 ou 501, ou après
 *   `MAX_FAILURES` échecs.
 *
 * En mocks MSW, la requête est servie par `src/mocks/client-errors-handlers.ts` : rien ne sort du
 * navigateur.
 */
export type ClientErrorKind = 'render' | 'query' | 'mutation' | 'other';

export type ClientErrorReport = {
  kind: ClientErrorKind;
  message: string;
  stack?: string;
  route?: string;
  userAgent?: string;
  traceId?: string;
  release?: string;
};

export const DEBOUNCE_MS = 2000;
export const MAX_PER_SESSION = 20;
export const MAX_FAILURES = 3;
const MAX_MESSAGE = 300;
const MAX_STACK = 4000;
const MAX_QUEUE = 10;
const SENT_KEY = 'graal.client-errors.sent';

type Send = (report: ClientErrorReport) => Promise<unknown>;
type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem'>;

export type ReporterOptions = {
  send?: Send;
  /** Une session authentifiée est-elle publiée vers `@graal/api` ? */
  canSend?: () => boolean;
  storage?: () => Storage | undefined;
  route?: () => string | undefined;
  userAgent?: () => string | undefined;
  release?: string;
  debounceMs?: number;
};

/** Une query string ou un fragment collé à un mot : retiré, le `:ligne:colonne` d'un cadre de pile gardé. */
const QUERY = /(?<=[\w/.~%-])[?#][^\s'"()<>[\]]+?((?::\d+){1,2})?(?=[\s'"()<>[\]]|$)/gu;
const TRACE_ID = /^[A-Za-z0-9._:-]{1,128}$/u;

export function stripQueries(value: string): string {
  return value.replace(QUERY, '$1');
}

function clip(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max)}…`;
}

/** La version au format accepté par l'API (`[A-Za-z0-9._+-]{1,64}`). */
export function toRelease(version: string): string | undefined {
  const cleaned = version.replace(/[^A-Za-z0-9._+-]/gu, '-').slice(0, 64);
  return cleaned || undefined;
}

/** Ce qui mérite un rapport : une panne, pas un refus ni une fonction coupée. */
export function isReportable(kind: ClientErrorKind, error: unknown): boolean {
  if (error instanceof ApiError) return error.status >= 500 && error.status !== 501 && !error.isBusy;
  // Une requête annulée (navigation, démontage) n'est pas une panne.
  if (error instanceof DOMException && error.name === 'AbortError') return false;
  return kind === 'render' || error instanceof Error;
}

function describe(error: unknown): { message: string; stack?: string; traceId?: string } {
  if (error instanceof ApiError) {
    const traceId = error.traceId && TRACE_ID.test(error.traceId) ? error.traceId : undefined;
    return {
      message: `HTTP ${error.status}${error.code ? ` ${error.code}` : ''}`,
      ...(traceId ? { traceId } : {}),
    };
  }
  if (error instanceof Error) {
    const message = `${error.name}: ${error.message}`;
    return { message, ...(error.stack ? { stack: error.stack } : {}) };
  }
  return { message: typeof error === 'string' ? error : 'Non-Error thrown' };
}

export function createClientErrorReporter(options: ReporterOptions = {}) {
  const send: Send = options.send ?? (() => Promise.resolve());
  const canSend = options.canSend ?? (() => true);
  const storage = options.storage ?? (() => undefined);
  const debounceMs = options.debounceMs ?? DEBOUNCE_MS;
  const seen = new Set<string>();
  const queue: ClientErrorReport[] = [];
  let timer: ReturnType<typeof setTimeout> | undefined;
  let failures = 0;
  let stopped = false;
  let flushing = false;

  const sentCount = (): number => {
    try {
      return Number(storage()?.getItem(SENT_KEY) ?? 0) || 0;
    } catch {
      return 0;
    }
  };
  const countSent = (): void => {
    try {
      storage()?.setItem(SENT_KEY, String(sentCount() + 1));
    } catch {
      // Stockage indisponible (navigation privée) : le plafond ne vaut alors que pour l'onglet.
    }
  };
  let sentHere = 0;
  const underCap = () => Math.max(sentCount(), sentHere) < MAX_PER_SESSION;

  async function flush(): Promise<void> {
    timer = undefined;
    if (flushing) return;
    flushing = true;
    try {
      while (queue.length > 0 && !stopped && underCap() && canSend()) {
        const report = queue.shift()!;
        try {
          await send(report);
          sentHere += 1;
          countSent();
          failures = 0;
        } catch (error) {
          // Un 429 (débit) ou un 501 (fonction coupée) ne changera pas d'ici la fin de la session.
          if (error instanceof ApiError && (error.status === 429 || error.status === 501)) stopped = true;
          failures += 1;
          if (failures >= MAX_FAILURES) stopped = true;
        }
      }
    } finally {
      flushing = false;
      // Arrêté, plafond atteint ou session perdue : ce qui reste ne partira pas.
      queue.length = 0;
    }
  }

  function report(kind: ClientErrorKind, error: unknown): void {
    if (stopped || !isReportable(kind, error) || !canSend() || !underCap()) return;
    const described = describe(error);
    const message = clip(stripQueries(described.message), MAX_MESSAGE);
    const stack = described.stack ? clip(stripQueries(described.stack), MAX_STACK) : undefined;
    const key = `${kind}|${message}|${stack?.split('\n', 2)[1] ?? ''}`;
    if (seen.has(key) || queue.length >= MAX_QUEUE) return;
    seen.add(key);
    const route = options.route?.();
    const userAgent = options.userAgent?.();
    queue.push({
      kind,
      message,
      ...(stack ? { stack } : {}),
      ...(route ? { route } : {}),
      ...(userAgent ? { userAgent: userAgent.slice(0, 512) } : {}),
      ...(described.traceId ? { traceId: described.traceId } : {}),
      ...(options.release ? { release: options.release } : {}),
    });
    if (timer !== undefined) clearTimeout(timer);
    timer = setTimeout(() => void flush(), debounceMs);
  }

  return { report, flush };
}

const safeSessionStorage = (): Storage | undefined => {
  try {
    return typeof sessionStorage === 'undefined' ? undefined : sessionStorage;
  } catch {
    return undefined;
  }
};

let reporter: ReturnType<typeof createClientErrorReporter> | undefined;

/** Le rapporteur de l'application : envoie à graal-api par le mutator (jeton et `X-Tenant` compris). */
function appReporter() {
  reporter ??= createClientErrorReporter({
    send: (report) =>
      apiFetch({ url: '/client-errors', method: 'POST', data: report, resource: 'clienterror' }),
    canSend: () => Boolean(getAccessToken() && getCurrentTenant()),
    storage: safeSessionStorage,
    route: () => (typeof window === 'undefined' ? undefined : window.location.pathname.slice(0, 256)),
    userAgent: () => (typeof navigator === 'undefined' ? undefined : navigator.userAgent),
    release: toRelease(__APP_VERSION__),
  });
  return reporter;
}

/** Signale une erreur à graal-api (voir l'en-tête du fichier). Ne lève jamais. */
export function reportClientError(kind: ClientErrorKind, error: unknown): void {
  try {
    appReporter().report(kind, error);
  } catch {
    // Le rapport d'une erreur ne doit jamais en provoquer une autre.
  }
}
